import { Dispatch, PropsWithChildren, SetStateAction, createContext, useCallback, useContext, useState } from 'react'

import axios from 'axios'

import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { TranscriptError } from 'src/models/Error'
import { getAlternativeRegionUrl, getViewMode } from 'src/utils/env'

type TranscriptErrorProviderProps = PropsWithChildren<{}>

type TranscriptErrorValue = {
    transcriptError: TranscriptError | null
    setTranscriptError: Dispatch<SetStateAction<TranscriptError | null>>
    transcriptErrorHandler: (error: unknown) => void
}

export const transcriptErrorsByStatusCode = new Map<number, TranscriptError>([
    [403, TranscriptError.ACCESS_DENIED],
    [404, TranscriptError.TRANSCRIPT_NOT_FOUND],
    [429, TranscriptError.SERVER_OVERLOAD],
    [503, TranscriptError.SERVER_OVERLOAD],
    [500, TranscriptError.TECHNICAL_DIFFICULTIES],
])

export const TranscriptErrorContext = createContext<TranscriptErrorValue | null>(null)

export const TranscriptErrorProvider = (props: TranscriptErrorProviderProps) => {
    const { children } = props
    const [transcriptError, setTranscriptError] = useState<TranscriptError | null>(null)

    const transcriptErrorHandler = useCallback((error: unknown) => {
        let errorType: TranscriptError | null = null
        if (axios.isAxiosError(error) && error.response) {
            if (transcriptErrorsByStatusCode.has(error.response.status)) {
                errorType = transcriptErrorsByStatusCode.get(error.response.status)!
            } else if (error.response.status === 401) {
                // when 401 is encountered, `HttpClientBase` will redirect user to the sign in page;
                // this takes time, so we set null here in this case, to prevent user from seeing
                // "Network Error" before being redirected
                errorType = null
            } else if (error.response.status === 400 && error.response.data?.ErrorType === 'BAD_SERVER_LOCATION') {
                // Show informative error screen while redirecting to correct region
                errorType = TranscriptError.REDIRECTING_TO_CORRECT_REGION
                window.location.assign(getAlternativeRegionUrl())
            }
        }
        if (!errorType) {
            errorType = getViewMode() === 'document' ? TranscriptError.DOCUMENT_PROCESSING_ERROR : TranscriptError.NETWORK_ERROR
        }
        setTranscriptError(errorType)
    }, [])

    const providedValue: TranscriptErrorValue = useShallowMemo({ transcriptError, setTranscriptError, transcriptErrorHandler })

    return <TranscriptErrorContext.Provider value={providedValue}>{children}</TranscriptErrorContext.Provider>
}

export const useTranscriptError = () => {
    const contextValue = useContext(TranscriptErrorContext)

    if (contextValue === null) {
        throw new Error('You have forgot to use <TranscriptErrorContext.Provider />, shame on you.')
    }

    return contextValue
}
